import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { bomberosMaterialMenorAPI, bomberosCompaniaAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, HardHat, Loader2 } from 'lucide-react';

const tiposEPP = [
    { value: 'casco', label: 'Casco' },
    { value: 'chaqueton', label: 'Chaquetón' },
    { value: 'pantalon', label: 'Pantalón' },
    { value: 'botas', label: 'Botas' },
    { value: 'guantes', label: 'Guantes' },
    { value: 'capucha', label: 'Capucha' },
    { value: 'equipo_respiracion', label: 'ERA (Equipo Respiración Autónoma)' },
    { value: 'linterna', label: 'Linterna' },
    { value: 'radio', label: 'Radio' },
    { value: 'arnes', label: 'Arnés' },
    { value: 'cuerda', label: 'Cuerda' },
    { value: 'hacha', label: 'Hacha' },
    { value: 'extintor', label: 'Extintor Portátil' },
];

const tallas = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

export default function EPPFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [companias, setCompanias] = useState([]);
    const [formData, setFormData] = useState({
        codigo: '',
        tipo: 'casco',
        marca: '',
        modelo: '',
        talla: '',
        numero_serie: '',
        fecha_adquisicion: '',
        fecha_vencimiento: '',
        estado: 'disponible',
        compania_id: '',
        observacion: '',
        activo: true,
    });

    useEffect(() => {
        loadCompanias();
        if (isEdit) {
            loadEPP();
        }
    }, [id]);

    const loadCompanias = async () => {
        try {
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
            if (!isEdit && res.data.length > 0 && !formData.compania_id) {
                setFormData(prev => ({ ...prev, compania_id: res.data[0].id }));
            }
        } catch (err) {
            toast.error('Error al cargar compañías');
        }
    };

    const loadEPP = async () => {
        setLoading(true);
        try {
            const res = await bomberosMaterialMenorAPI.getAll({ id });
            const item = res.data.find(m => m.id === id);
            if (item) {
                setFormData({
                    codigo: item.codigo || '',
                    tipo: item.tipo || 'casco',
                    marca: item.marca || '',
                    modelo: item.modelo || '',
                    talla: item.talla || '',
                    numero_serie: item.numero_serie || '',
                    fecha_adquisicion: item.fecha_adquisicion || '',
                    fecha_vencimiento: item.fecha_vencimiento || '',
                    estado: item.estado || 'disponible',
                    compania_id: item.compania_id || '',
                    observacion: item.observacion || '',
                    activo: item.activo !== false,
                });
            }
        } catch (err) {
            toast.error('Error al cargar EPP');
            navigate('/bomberos/epp');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const generateCodigo = () => {
        const prefix = formData.tipo.substring(0, 3).toUpperCase();
        const random = Math.floor(Math.random() * 9000) + 1000;
        handleChange('codigo', `${prefix}-${random}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.codigo || !formData.tipo || !formData.compania_id) {
            toast.error('Complete los campos obligatorios');
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await bomberosMaterialMenorAPI.update(id, formData);
                toast.success('EPP actualizado correctamente');
            } else {
                await bomberosMaterialMenorAPI.create({
                    ...formData,
                    empresa_id: localStorage.getItem('empresa_id') || ''
                });
                toast.success('EPP creado correctamente');
            }
            navigate('/bomberos/epp');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/epp')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <HardHat className="h-6 w-6" />
                        {isEdit ? 'Editar EPP' : 'Nuevo EPP / Material Menor'}
                    </h1>
                    <p className="text-muted-foreground">
                        {isEdit ? 'Modifique los datos del equipo' : 'Complete los datos para registrar nuevo equipamiento'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Identificación */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Identificación</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="tipo">Tipo de Equipamiento *</Label>
                            <Select value={formData.tipo} onValueChange={(v) => handleChange('tipo', v)}>
                                <SelectTrigger data-testid="select-tipo">
                                    <SelectValue placeholder="Seleccione tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tiposEPP.map(t => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="codigo">Código de Inventario *</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="codigo"
                                    placeholder="CAS-0001"
                                    value={formData.codigo}
                                    onChange={(e) => handleChange('codigo', e.target.value.toUpperCase())}
                                    data-testid="input-codigo"
                                />
                                <Button type="button" variant="outline" onClick={generateCodigo} className="shrink-0">
                                    Auto
                                </Button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="numero_serie">Número de Serie</Label>
                            <Input
                                id="numero_serie"
                                placeholder="S/N del fabricante"
                                value={formData.numero_serie}
                                onChange={(e) => handleChange('numero_serie', e.target.value)}
                                data-testid="input-serie"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Datos del Equipo */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Datos del Equipo</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="marca">Marca</Label>
                            <Input
                                id="marca"
                                placeholder="MSA, Drager, Bullard..."
                                value={formData.marca}
                                onChange={(e) => handleChange('marca', e.target.value)}
                                data-testid="input-marca"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="modelo">Modelo</Label>
                            <Input
                                id="modelo"
                                placeholder="Modelo del equipo"
                                value={formData.modelo}
                                onChange={(e) => handleChange('modelo', e.target.value)}
                                data-testid="input-modelo"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="talla">Talla</Label>
                            <Select value={formData.talla || 'none'} onValueChange={(v) => handleChange('talla', v === 'none' ? '' : v)}>
                                <SelectTrigger data-testid="select-talla">
                                    <SelectValue placeholder="Seleccione talla" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin talla</SelectItem>
                                    {tallas.map(t => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="compania_id">Compañía *</Label>
                            <Select value={formData.compania_id} onValueChange={(v) => handleChange('compania_id', v)}>
                                <SelectTrigger data-testid="select-compania">
                                    <SelectValue placeholder="Seleccione compañía" />
                                </SelectTrigger>
                                <SelectContent>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Fechas y Estado */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Estado y Vigencia</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="estado">Estado Actual</Label>
                            <Select value={formData.estado} onValueChange={(v) => handleChange('estado', v)}>
                                <SelectTrigger data-testid="select-estado">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="disponible">Disponible</SelectItem>
                                    <SelectItem value="asignado">Asignado</SelectItem>
                                    <SelectItem value="mantenimiento">En Mantenimiento</SelectItem>
                                    <SelectItem value="baja">Dado de Baja</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_adquisicion">Fecha de Adquisición</Label>
                            <Input
                                id="fecha_adquisicion"
                                type="date"
                                value={formData.fecha_adquisicion}
                                onChange={(e) => handleChange('fecha_adquisicion', e.target.value)}
                                data-testid="input-adquisicion"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_vencimiento">Fecha de Vencimiento</Label>
                            <Input
                                id="fecha_vencimiento"
                                type="date"
                                value={formData.fecha_vencimiento}
                                onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
                                data-testid="input-vencimiento"
                            />
                            <p className="text-xs text-muted-foreground">Para EPP con vida útil limitada</p>
                        </div>
                        <div className="space-y-2 md:col-span-3">
                            <Label htmlFor="observacion">Observaciones</Label>
                            <Textarea
                                id="observacion"
                                placeholder="Notas adicionales sobre el equipo"
                                value={formData.observacion}
                                onChange={(e) => handleChange('observacion', e.target.value)}
                                rows={2}
                                data-testid="input-observacion"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button type="button" variant="outline" onClick={() => navigate('/bomberos/epp')}>
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="submit-btn">
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                {isEdit ? 'Actualizar' : 'Crear EPP'}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
        </>
    );
}
